import { useState } from 'react'
import clsx from 'clsx'
import { motion, type Transition } from 'motion/react'
import * as RadixTabs from '@radix-ui/react-tabs'
import { yStack } from '../../patterns/stack'
import { framerTransitions } from '../../theme/transitions'
import { Button } from '../Button/Button'
import { Text } from '../Text/Text'
import { ComparisonTable } from './ComparisonTable'
import { type ComparisonTableProps } from './ComparisonTable.types'
import { visuallyHidden } from '../../utils/visibility.css'
import { tabButton, tabsList } from './MobileComparisonTable.css'

const TAB_CONTENT_TRANSITION: Transition = {
  duration: framerTransitions.defaultDuration,
}

export type MobileComparisonTableProps = ComparisonTableProps & {
  defaultSelectedColumn?: string | null
  className?: string
}

export function MobileComparisonTable({
  head,
  body,
  defaultSelectedColumn,
  caption,
  className,
}: MobileComparisonTableProps) {
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(() => {
    if (defaultSelectedColumn) {
      const columnIndex = head.findIndex((headerValue) => headerValue === defaultSelectedColumn)
      if (columnIndex !== -1) {
        return columnIndex
      }
    }

    // First header value is usually an Markers.EmptyHeader.
    return 1
  })

  const firstColumnHeading = head[0] ?? ''
  // First column heading is not converted into a tab, neither we should have a tab content for it
  const headings = head.slice(1)

  const tabs = (
    <RadixTabs.List className={tabsList}>
      {headings.map((heading) => {
        return (
          <RadixTabs.Trigger key={heading} value={heading} asChild={true}>
            <Button className={tabButton} variant="secondary" size="small">
              {heading}
            </Button>
          </RadixTabs.Trigger>
        )
      })}
    </RadixTabs.List>
  )

  return (
    <RadixTabs.Root
      className={clsx(yStack({ gap: 'sm' }), className)}
      value={head[selectedColumnIndex]}
      onValueChange={(value) => setSelectedColumnIndex(head.findIndex((tier) => tier === value))}
    >
      {firstColumnHeading ? (
        <div className={yStack({ gap: 'md' })}>
          <Text size="md" color="textPrimary">
            {firstColumnHeading}
          </Text>

          {tabs}
        </div>
      ) : (
        tabs
      )}

      {headings.map((heading) => {
        return (
          <TabContent key={heading} value={heading} data-value={heading}>
            <ComparisonTable.Root caption={caption}>
              {/* Table header is built only to serve accessibility purposes */}
              <ComparisonTable.Head className={visuallyHidden}>
                <ComparisonTable.Row>
                  <ComparisonTable.Header>{firstColumnHeading}</ComparisonTable.Header>
                  <ComparisonTable.Header>{heading}</ComparisonTable.Header>
                </ComparisonTable.Row>
              </ComparisonTable.Head>

              <ComparisonTable.Body>
                {body.map((row) => {
                  const [rowTitle, ...values] = row
                  // 'selectedColumnIndex' is defined taking the whole table into account. However,
                  // 'values' is a subset of the table without the first column, so we need to subtract 1
                  // from the index.
                  const value = values[selectedColumnIndex - 1]

                  return (
                    <ComparisonTable.Row key={rowTitle.title}>
                      <ComparisonTable.TitleDataCell
                        title={rowTitle.title}
                        description={rowTitle.description}
                        coverageList={rowTitle.coverageList}
                      />
                      <ComparisonTable.DataCell alignment="right">{value}</ComparisonTable.DataCell>
                    </ComparisonTable.Row>
                  )
                })}
              </ComparisonTable.Body>
            </ComparisonTable.Root>
          </TabContent>
        )
      })}
    </RadixTabs.Root>
  )
}

function TabContent({ children, ...others }: RadixTabs.TabsContentProps) {
  return (
    <RadixTabs.Content {...others}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: TAB_CONTENT_TRANSITION,
        }}
      >
        {children}
      </motion.div>
    </RadixTabs.Content>
  )
}
